import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon
} from "@mui/icons-material";
import supabase from "../../services/supabaseClient";
import { useCategories, useFetchCategories } from "../../actions/categories";
import { captureError } from "../../lib/errorReporting";

const buildLocalDefinition = (definition) => ({
    categoryName: definition.categoryName,
    originalName: definition.categoryName,
    subcategories: Array.isArray(definition.subcategories)
        ? [...definition.subcategories]
        : [],
    draftSubcategory: "",
    isNew: Boolean(definition.isNew)
});

const createEmptyDefinition = () => ({
    categoryName: "",
    originalName: null,
    subcategories: [],
    draftSubcategory: "",
    isNew: true
});

export function CategoryDefinitionsAdmin() {
    const categories = useCategories();
    const fetchCategories = useFetchCategories();

    const [definitions, setDefinitions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    useEffect(() => {
        if (categories.length === 0) {
            fetchCategories();
            return;
        }
        setDefinitions(categories.map((item) => buildLocalDefinition(item)));
    }, [categories, fetchCategories]);

    const hasUnsavedChanges = useMemo(() => {
        if (categories.length !== definitions.length) {
            return true;
        }
        return definitions.some((definition, index) => {
            const original = categories[index];
            if (!original) return true;
            if (definition.categoryName !== original.categoryName) {
                return true;
            }
            const nextSubs = definition.subcategories.slice().sort();
            const originalSubs = Array.isArray(original.subcategories)
                ? [...original.subcategories].sort()
                : [];
            if (nextSubs.length !== originalSubs.length) {
                return true;
            }
            return nextSubs.some((value, idx) => value !== originalSubs[idx]);
        });
    }, [categories, definitions]);

    const resetStatus = () => {
        setStatus("");
        setError("");
    };

    const handleCategoryNameChange = (index, value) => {
        setDefinitions((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                categoryName: value
            };
            return next;
        });
        resetStatus();
    };

    const handleDraftChange = (index, value) => {
        setDefinitions((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                draftSubcategory: value
            };
            return next;
        });
        resetStatus();
    };

    const handleAddSubcategory = (index) => {
        setDefinitions((prev) => {
            const target = prev[index];
            if (!target) return prev;
            const draft = target.draftSubcategory.trim();
            if (!draft) return prev;
            if (target.subcategories.includes(draft)) {
                return prev;
            }
            const next = [...prev];
            next[index] = {
                ...target,
                subcategories: [...target.subcategories, draft].sort((a, b) => a.localeCompare(b)),
                draftSubcategory: ""
            };
            return next;
        });
        resetStatus();
    };

    const handleRemoveSubcategory = (categoryIndex, subcategory) => {
        setDeleteConfirm({ categoryIndex, subcategory });
    };

    const handleRenameSubcategory = async (categoryIndex, oldName, newName) => {
        const trimmed = newName.trim();
        if (!trimmed || trimmed === oldName) return;

        const definition = definitions[categoryIndex];
        if (!definition) return;

        if (definition.subcategories.includes(trimmed)) {
            setError(`"${trimmed}" already exists in ${definition.categoryName}.`);
            return;
        }

        setSaving(true);
        resetStatus();
        try {
            // Update bar_categories_prod across all bars
            const { data: updatedCount, error: rpcError } = await supabase.rpc(
                "rename_subcategory_in_category",
                {
                    p_category: definition.categoryName,
                    p_old_name: oldName,
                    p_new_name: trimmed
                }
            );
            if (rpcError) throw rpcError;

            // Update category_definitions_prod
            const updatedSubs = definition.subcategories
                .map((s) => (s === oldName ? trimmed : s))
                .sort((a, b) => a.localeCompare(b));

            const { error: upsertError } = await supabase
                .from("category_definitions_prod")
                .upsert(
                    { category_name: definition.categoryName, subcategories: updatedSubs },
                    { onConflict: "category_name" }
                );
            if (upsertError) throw upsertError;

            await fetchCategories();
            const barMsg = typeof updatedCount === "number" ? ` (${updatedCount} bar${updatedCount === 1 ? "" : "s"} updated)` : "";
            setStatus(`Renamed "${oldName}" to "${trimmed}"${barMsg}`);
        } catch (renameProblem) {
            captureError(renameProblem, { view: "CategoryDefinitionsAdmin", action: "renameSubcategory" });
            setError(renameProblem.message || "Unable to rename subcategory.");
        } finally {
            setSaving(false);
        }
    };

    const handleReassignSubcategory = async (categoryIndex, subcategory, targetCategoryName) => {
        const definition = definitions[categoryIndex];
        if (!definition || definition.categoryName === targetCategoryName) return;

        setSaving(true);
        resetStatus();
        try {
            // Move in all bars via existing RPC
            const { data: movedCount, error: rpcError } = await supabase.rpc(
                "change_subcategory_to_category",
                {
                    old_category: definition.categoryName,
                    new_category: targetCategoryName,
                    target_subcategory: subcategory
                }
            );
            if (rpcError) throw rpcError;

            // Update category_definitions_prod: remove from old, add to new
            const oldSubs = definition.subcategories.filter((s) => s !== subcategory);
            const targetDef = definitions.find((d) => d.categoryName === targetCategoryName);
            const newSubs = targetDef
                ? [...(targetDef.subcategories || []).filter((s) => s !== subcategory), subcategory].sort((a, b) => a.localeCompare(b))
                : [subcategory];

            const { error: upsertError } = await supabase
                .from("category_definitions_prod")
                .upsert([
                    { category_name: definition.categoryName, subcategories: oldSubs },
                    { category_name: targetCategoryName, subcategories: newSubs }
                ], { onConflict: "category_name" });
            if (upsertError) throw upsertError;

            await fetchCategories();
            const barMsg = typeof movedCount === "number" ? ` (${movedCount} bar${movedCount === 1 ? "" : "s"} updated)` : "";
            setStatus(`Reassigned "${subcategory}" from ${definition.categoryName} to ${targetCategoryName}${barMsg}`);
        } catch (reassignProblem) {
            captureError(reassignProblem, { view: "CategoryDefinitionsAdmin", action: "reassignSubcategory" });
            setError(reassignProblem.message || "Unable to reassign subcategory.");
        } finally {
            setSaving(false);
        }
    };

    const confirmDeleteSubcategory = () => {
        if (!deleteConfirm) return;
        const { categoryIndex, subcategory } = deleteConfirm;
        setDefinitions((prev) => {
            const target = prev[categoryIndex];
            if (!target) return prev;
            const next = [...prev];
            next[categoryIndex] = {
                ...target,
                subcategories: target.subcategories.filter((item) => item !== subcategory)
            };
            return next;
        });
        resetStatus();
        setDeleteConfirm(null);
    };

    const handleAddCategory = () => {
        setDefinitions((prev) => [...prev, createEmptyDefinition()]);
        resetStatus();
    };

    const handleRemoveCategory = async (definition) => {
        resetStatus();
        if (!definition?.categoryName) {
            setDefinitions((prev) => prev.filter((item) => item !== definition));
            return;
        }
        setSaving(true);
        try {
            const { error: deleteError } = await supabase
                .from("category_definitions_prod")
                .delete()
                .eq("category_name", definition.categoryName);
            if (deleteError) {
                throw deleteError;
            }
            await fetchCategories();
            setStatus(`Deleted ${definition.categoryName}`);
        } catch (deleteProblem) {
            captureError(deleteProblem, { view: "CategoryDefinitionsAdmin", action: "deleteCategory" });
            setError(deleteProblem.message || "Unable to delete category right now.");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAll = async () => {
        resetStatus();

        const originalBySubcategory = new Map();
        categories.forEach((category) => {
            (category.subcategories || []).forEach((sub) => {
                originalBySubcategory.set(sub, category.categoryName);
            });
        });

        const payload = [];
        const renamed = [];
        const nextBySubcategory = new Map();

        for (const definition of definitions) {
            const trimmedName = definition.categoryName.trim();
            if (!trimmedName) {
                setError("Category names cannot be empty.");
                return;
            }

            const filteredSubs = definition.subcategories
                .map((item) => item.trim())
                .filter((item) => item.length > 0);

            payload.push({
                category_name: trimmedName,
                subcategories: filteredSubs
            });

            filteredSubs.forEach((sub) => {
                nextBySubcategory.set(sub, trimmedName);
            });

            if (
                definition.originalName &&
                definition.originalName !== trimmedName
            ) {
                renamed.push(definition.originalName);
            }
        }

        const categoryMoves = [];
        originalBySubcategory.forEach((oldCategory, subcategory) => {
            const updatedCategory = nextBySubcategory.get(subcategory);
            if (updatedCategory && updatedCategory !== oldCategory) {
                categoryMoves.push({
                    subcategory,
                    oldCategory,
                    newCategory: updatedCategory
                });
            }
        });

        setSaving(true);
        try {
            const { error: upsertError } = await supabase
                .from("category_definitions_prod")
                .upsert(payload, { onConflict: "category_name" });
            if (upsertError) {
                throw upsertError;
            }

            for (const oldName of renamed) {
                await supabase
                    .from("category_definitions_prod")
                    .delete()
                    .eq("category_name", oldName);
            }

            let totalUpdated = 0;
            for (const move of categoryMoves) {
                const { data: movedCount, error: moveError } = await supabase.rpc(
                    "change_subcategory_to_category",
                    {
                        old_category: move.oldCategory,
                        new_category: move.newCategory,
                        target_subcategory: move.subcategory
                    }
                );
                if (moveError) {
                    throw moveError;
                }
                if (typeof movedCount === "number") {
                    totalUpdated += movedCount;
                }
            }

            await fetchCategories();

            const updateMessage = categoryMoves.length > 0
                ? ` Category assignments updated for ${totalUpdated} bar${totalUpdated === 1 ? "" : "s"}.`
                : "";

            setStatus(`Category definitions saved.${updateMessage}`);
        } catch (saveProblem) {
            captureError(saveProblem, { view: "CategoryDefinitionsAdmin", action: "saveAllCategories" });
            setError(saveProblem.message || "Unable to save categories right now.");
        } finally {
            setSaving(false);
        }
    };

    const handleRefresh = () => {
        resetStatus();
        fetchCategories();
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" gap={1}>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={saving}
                    >
                        Refresh
                    </Button>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddCategory}
                        disabled={saving}
                    >
                        Add Category
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveAll}
                        disabled={saving || definitions.length === 0 || !hasUnsavedChanges}
                    >
                        Save Changes
                    </Button>
                </Box>
            </Box>

            {status && (
                <Alert severity="success">{status}</Alert>
            )}
            {error && (
                <Alert severity="error">{error}</Alert>
            )}

            {definitions.length === 0 ? (
                <Paper elevation={0} sx={{ padding: 4, textAlign: "center" }}>
                    <Typography variant="body1" color="textSecondary">
                        No category definitions found.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {definitions.map((definition, index) => (
                        <Paper key={definition.categoryName || `draft-${index}`} sx={{ padding: 2 }}>
                            <Stack spacing={2}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <TextField
                                        label="Category Name"
                                        value={definition.categoryName}
                                        onChange={(event) =>
                                            handleCategoryNameChange(index, event.target.value)
                                        }
                                        fullWidth
                                    />
                                    <IconButton
                                        aria-label="remove category"
                                        onClick={() => handleRemoveCategory(definition)}
                                        disabled={saving}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                                <Divider />
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    {definition.subcategories.map((subcategory) => (
                                        <SubcategoryChip
                                            key={subcategory}
                                            label={subcategory}
                                            onDelete={() => handleRemoveSubcategory(index, subcategory)}
                                            onRename={(newName) => handleRenameSubcategory(index, subcategory, newName)}
                                            onReassign={(targetCategory) => handleReassignSubcategory(index, subcategory, targetCategory)}
                                            otherCategories={definitions
                                                .filter((d) => d.categoryName !== definition.categoryName && d.categoryName.trim())
                                                .map((d) => d.categoryName)}
                                            disabled={saving}
                                        />
                                    ))}
                                </Stack>
                                <Box display="flex" gap={1}>
                                    <TextField
                                        label="Add subcategory"
                                        value={definition.draftSubcategory}
                                        onChange={(event) =>
                                            handleDraftChange(index, event.target.value)
                                        }
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                handleAddSubcategory(index);
                                            }
                                        }}
                                        fullWidth
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleAddSubcategory(index)}
                                        disabled={saving}
                                    >
                                        Add
                                    </Button>
                                </Box>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <Dialog
                open={Boolean(deleteConfirm)}
                onClose={() => setDeleteConfirm(null)}
            >
                <DialogTitle>Delete Subcategory</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete &quot;{deleteConfirm?.subcategory}&quot;? This will remove it from the category definition. You will still need to save changes for this to take effect across bars.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button onClick={confirmDeleteSubcategory} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function SubcategoryChip({ label, onDelete, onRename, onReassign, otherCategories, disabled }) {
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(label);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [reassignConfirm, setReassignConfirm] = useState(null);

    const handleStartEdit = () => {
        setEditValue(label);
        setEditing(true);
        setReassignOpen(false);
        setReassignConfirm(null);
    };

    const handleCancel = () => {
        setEditValue(label);
        setEditing(false);
        setReassignOpen(false);
    };

    const handleConfirm = () => {
        const trimmed = editValue.trim();
        if (trimmed && trimmed !== label) {
            onRename(trimmed);
        }
        setEditing(false);
        setReassignOpen(false);
    };

    if (editing) {
        return (
            <Box sx={{ display: "inline-flex", flexDirection: "column", position: "relative" }}>
                <Paper
                    elevation={1}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "2px 8px",
                        gap: 0.5,
                        height: 32
                    }}
                >
                    <TextField
                        size="small"
                        variant="standard"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleConfirm();
                            } else if (e.key === "Escape") {
                                handleCancel();
                            }
                        }}
                        autoFocus
                        sx={{ minWidth: 80 }}
                        inputProps={{ style: { fontSize: 14 } }}
                    />
                    <IconButton size="small" onClick={handleConfirm} color="success" aria-label="confirm rename">
                        <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancel} aria-label="cancel rename">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Paper>
                {otherCategories && otherCategories.length > 0 && (
                    <Box sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10 }}>
                        <Paper
                            variant="outlined"
                            onClick={() => setReassignOpen((prev) => !prev)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 1,
                                py: 0.25,
                                cursor: "pointer",
                                userSelect: "none",
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0
                            }}
                        >
                            <Typography variant="body2">Reassign</Typography>
                            <ExpandMoreIcon
                                sx={{
                                    fontSize: 16,
                                    ml: 0.5,
                                    transform: reassignOpen ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.2s"
                                }}
                            />
                        </Paper>
                        <Collapse in={reassignOpen}>
                            <Paper variant="outlined" sx={{ borderTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                                <List dense disablePadding>
                                    {otherCategories.map((cat) => (
                                        <ListItemButton
                                            key={cat}
                                            onClick={() => setReassignConfirm(cat)}
                                        >
                                            <ListItemText primary={cat} primaryTypographyProps={{ variant: "body2" }} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Paper>
                        </Collapse>
                    </Box>
                )}
                <Dialog
                    open={Boolean(reassignConfirm)}
                    onClose={() => setReassignConfirm(null)}
                >
                    <DialogTitle>Reassign Subcategory?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Move <strong>{label}</strong> to <strong>{reassignConfirm}</strong>? This will update all bars that use this subcategory.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setReassignConfirm(null)} color="primary">
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                onReassign(reassignConfirm);
                                setReassignConfirm(null);
                                setEditing(false);
                                setReassignOpen(false);
                            }}
                            color="primary"
                            variant="contained"
                        >
                            Proceed
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    return (
        <Paper
            elevation={1}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 8px",
                gap: 0.5,
                height: 32
            }}
        >
            <Typography variant="body2">{label}</Typography>
            <IconButton size="small" onClick={handleStartEdit} disabled={disabled} aria-label="edit subcategory">
                <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onDelete} disabled={disabled} aria-label="remove subcategory">
                <DeleteIcon fontSize="small" />
            </IconButton>
        </Paper>
    );
}

export default CategoryDefinitionsAdmin;
